import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search, 
  Filter, 
  Eye, 
  Download,
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  Music,
  Award,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ParticipantAnalytics } from './participant-analytics';

export interface ParticipantRecord {
  id: string;
  userId: string;
  age?: number;
  ageRange?: string;
  musicalGenres: string[];
  musicalEngagement: 'casual' | 'enthusiast' | 'musician' | 'professional';
  formalTrainingYears: number;
  listeningDevice: string;
  listeningContext: 'alone' | 'with-others';
  environment: string;
  aiMusicExperience: string;
  annotationCount: number;
  createdAt: string;
  updatedAt: string;
  feedbackMetrics?: {
    feedbackCount: number;
    averageFeedbackLength: number;
    feedbackByContext: Record<string, number>;
    lastFeedbackDate?: string;
  };
}

interface ParticipantFilters {
  search: string;
  musicalEngagement?: string;
  ageRange?: string;
  minAnnotations?: number;
  maxAnnotations?: number;
  dateRange?: string;
}

interface ParticipantBrowserProps {
  onViewDetails?: (participant: ParticipantRecord) => void;
  onExport?: (filters: ParticipantFilters) => void;
  className?: string;
}

// Mock data for demonstration
const mockParticipants: ParticipantRecord[] = [
  {
    id: 'part_001',
    userId: 'user_001',
    age: 28,
    ageRange: '25-34',
    musicalGenres: ['Rock', 'Electronic', 'Jazz'],
    musicalEngagement: 'enthusiast',
    formalTrainingYears: 3,
    listeningDevice: 'Headphones (over-ear)',
    listeningContext: 'alone',
    environment: 'Quiet room',
    aiMusicExperience: 'Tried once or twice',
    annotationCount: 47,
    createdAt: '2024-01-10T09:30:00Z',
    updatedAt: '2024-01-15T14:22:00Z'
  },
  {
    id: 'part_002',
    userId: 'user_002',
    ageRange: '35-44',
    musicalGenres: ['Classical', 'Folk', 'World-music'],
    musicalEngagement: 'musician',
    formalTrainingYears: 12,
    listeningDevice: 'External speakers',
    listeningContext: 'alone',
    environment: 'Quiet room',
    aiMusicExperience: 'Never used AI music tools',
    annotationCount: 23,
    createdAt: '2024-01-12T16:45:00Z',
    updatedAt: '2024-01-14T11:30:00Z'
  },
  {
    id: 'part_003',
    userId: 'user_003',
    age: 22,
    ageRange: '18-24',
    musicalGenres: ['Hip-Hop', 'R&B', 'Pop'],
    musicalEngagement: 'casual',
    formalTrainingYears: 0,
    listeningDevice: 'Earbuds/In-ear',
    listeningContext: 'with-others',
    environment: 'Public transportation',
    aiMusicExperience: 'Use regularly',
    annotationCount: 12,
    createdAt: '2024-01-14T20:15:00Z',
    updatedAt: '2024-01-15T09:45:00Z'
  }
];

function ParticipantDetailsModal({ 
  participant, 
  onClose 
}: { 
  participant: ParticipantRecord; 
  onClose: () => void;
}) {
  const getEngagementColor = (engagement: string): string => {
    switch (engagement) {
      case 'professional': return 'text-purple-600';
      case 'musician': return 'text-blue-600';
      case 'enthusiast': return 'text-green-600';
      case 'casual': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getEngagementIcon = (engagement: string) => {
    switch (engagement) {
      case 'professional': return <Award className="h-4 w-4" />;
      case 'musician': return <Music className="h-4 w-4" />;
      case 'enthusiast': return <Users className="h-4 w-4" />;
      case 'casual': return <Clock className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Participant Details
            </CardTitle>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Basic Information</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Participant ID:</strong> <code className="text-xs bg-muted px-1 rounded">{participant.id}</code></div>
                <div><strong>User ID:</strong> <code className="text-xs bg-muted px-1 rounded">{participant.userId}</code></div>
                {participant.age && <div><strong>Age:</strong> {participant.age}</div>}
                {participant.ageRange && <div><strong>Age Range:</strong> {participant.ageRange}</div>}
                <div><strong>Created:</strong> {new Date(participant.createdAt).toLocaleString()}</div>
                <div><strong>Last Updated:</strong> {new Date(participant.updatedAt).toLocaleString()}</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Participation Stats</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <strong>Annotations:</strong> 
                  <Badge variant="outline">{participant.annotationCount}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <strong>Musical Engagement:</strong>
                  <div className={cn("flex items-center gap-1", getEngagementColor(participant.musicalEngagement))}>
                    {getEngagementIcon(participant.musicalEngagement)}
                    <Badge variant="outline" className="capitalize">
                      {participant.musicalEngagement}
                    </Badge>
                  </div>
                </div>
                <div><strong>Formal Training:</strong> {participant.formalTrainingYears} years</div>
              </div>
            </div>
          </div>

          {/* Musical Background */}
          <div>
            <h3 className="font-semibold mb-3">Musical Background</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Preferred Genres:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {participant.musicalGenres.map((genre, index) => (
                    <Badge key={index} variant="secondary">
                      {genre}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label>Musical Engagement Level:</Label>
                  <div className={cn("flex items-center gap-2 mt-1", getEngagementColor(participant.musicalEngagement))}>
                    {getEngagementIcon(participant.musicalEngagement)}
                    <span className="capitalize font-medium">{participant.musicalEngagement}</span>
                  </div>
                </div>
                <div>
                  <Label>Formal Training:</Label>
                  <p className="mt-1">{participant.formalTrainingYears} years</p>
                </div>
              </div>
            </div>
          </div>

          {/* Listening Environment */}
          <div>
            <h3 className="font-semibold mb-3">Listening Environment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <Label>Listening Device:</Label>
                <Badge variant="outline" className="ml-2">
                  {participant.listeningDevice}
                </Badge>
              </div>
              <div>
                <Label>Listening Context:</Label>
                <Badge variant="outline" className="ml-2 capitalize">
                  {participant.listeningContext}
                </Badge>
              </div>
              <div>
                <Label>Environment:</Label>
                <Badge variant="outline" className="ml-2">
                  {participant.environment}
                </Badge>
              </div>
              <div>
                <Label>AI Music Experience:</Label>
                <Badge variant="outline" className="ml-2">
                  {participant.aiMusicExperience}
                </Badge>
              </div>
            </div>
          </div>

          {/* Participation Quality Metrics */}
          <div>
            <h3 className="font-semibold mb-3">Participation Quality</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="text-center">
                <div className="text-lg font-bold text-primary">
                  {participant.annotationCount}
                </div>
                <div className="text-xs text-muted-foreground">Total Annotations</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">
                  {participant.annotationCount > 20 ? 'High' : participant.annotationCount > 10 ? 'Medium' : 'Low'}
                </div>
                <div className="text-xs text-muted-foreground">Engagement Level</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">
                  {Math.floor((Date.now() - new Date(participant.createdAt).getTime()) / (1000 * 60 * 60 * 24))}
                </div>
                <div className="text-xs text-muted-foreground">Days Active</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ParticipantBrowser({ onViewDetails, onExport, className }: ParticipantBrowserProps) {
  const [participants, setParticipants] = useState<ParticipantRecord[]>(mockParticipants);
  const [filters, setFilters] = useState<ParticipantFilters>({ search: '' });
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantRecord | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  const itemsPerPage = 10;
  const totalPages = Math.ceil(participants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentParticipants = participants.slice(startIndex, endIndex);

  const loadParticipants = async () => {
    setIsLoading(true);
    try {
      // Use real API call with filters
      const { adminService } = await import('@/client/services/admin');
      const response = await adminService.getParticipants();
      setParticipants(response.participants);
    } catch (error) {
      console.error('Failed to load participants:', error);
      // Fallback to mock data if API fails
      setParticipants(mockParticipants);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadParticipants();
  }, [filters]);

  const handleViewDetails = (participant: ParticipantRecord) => {
    setSelectedParticipant(participant);
    onViewDetails?.(participant);
  };

  const handleExport = () => {
    onExport?.(filters);
  };

  const getEngagementBadge = (engagement: string) => {
    const variants = {
      'professional': 'default',
      'musician': 'secondary',
      'enthusiast': 'outline',
      'casual': 'outline'
    } as const;
    
    return (
      <Badge variant={variants[engagement as keyof typeof variants] || 'outline'}>
        {engagement}
      </Badge>
    );
  };

  const getAnnotationCountColor = (count: number): string => {
    if (count >= 30) return 'text-green-600';
    if (count >= 15) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Participant Management</h2>
          <p className="text-muted-foreground">
            View and manage research participants
          </p>
        </div>
        <Button onClick={handleExport} className="gap-2">
          <Download className="h-4 w-4" />
          Export Participants
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Participant ID, User ID..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-8"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="engagement">Musical Engagement</Label>
              <Select
                value={filters.musicalEngagement || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, musicalEngagement: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="musician">Musician</SelectItem>
                  <SelectItem value="enthusiast">Enthusiast</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="ageRange">Age Range</Label>
              <Select
                value={filters.ageRange || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, ageRange: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ages</SelectItem>
                  <SelectItem value="13-17">13-17</SelectItem>
                  <SelectItem value="18-24">18-24</SelectItem>
                  <SelectItem value="25-34">25-34</SelectItem>
                  <SelectItem value="35-44">35-44</SelectItem>
                  <SelectItem value="45-54">45-54</SelectItem>
                  <SelectItem value="55-64">55-64</SelectItem>
                  <SelectItem value="65+">65+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="dateRange">Registration Date</Label>
              <Select
                value={filters.dateRange || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, dateRange: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This week</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Participant Analytics */}
      <ParticipantAnalytics participants={participants} />

      {/* Results Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Participants ({participants.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Musical Engagement</TableHead>
                  <TableHead>Training</TableHead>
                  <TableHead>Annotations</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentParticipants.map((participant) => (
                  <TableRow key={participant.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{participant.id}</div>
                        <div className="text-sm text-muted-foreground">
                          {participant.userId}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {participant.age || participant.ageRange || 'Not specified'}
                    </TableCell>
                    <TableCell>
                      {getEngagementBadge(participant.musicalEngagement)}
                    </TableCell>
                    <TableCell>
                      {participant.formalTrainingYears} years
                    </TableCell>
                    <TableCell>
                      <div className={cn("font-medium", getAnnotationCountColor(participant.annotationCount))}>
                        {participant.annotationCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(participant.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(participant)}
                        className="gap-1"
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Modal */}
      {selectedParticipant && (
        <ParticipantDetailsModal
          participant={selectedParticipant}
          onClose={() => setSelectedParticipant(null)}
        />
      )}
    </div>
  );
}